/**
 * ════════════════════════════════════════════════════════════
 * DEVBRAINS — SOCIAL AUTH HOOK
 * ════════════════════════════════════════════════════════════
 *
 * Supports:
 *   - Google    (Google Identity Services / OAuth 2.0)
 *   - LinkedIn  (OAuth 2.0 redirect flow)
 *   - GitHub    (OAuth 2.0 redirect flow)
 *
 * ── HOW IT WORKS ────────────────────────────────────────────
 *
 *  Google:
 *    Frontend uses @react-oauth/google → gets credential (JWT)
 *    → POST /api/auth/google { credential }
 *    → Backend verifies JWT with Google, creates/finds user
 *    → Returns DevBrains JWT token
 *
 *  LinkedIn / GitHub:
 *    Frontend redirects to provider OAuth URL
 *    → Provider redirects back to /auth/callback?code=xxx&provider=github
 *    → OAuthCallback page POSTs code to backend
 *    → Backend exchanges code for access_token, fetches profile
 *    → Returns DevBrains JWT token
 *
 * ── TODO ────────────────────────────────────────────────────
 *   See SOCIAL_AUTH.md for full setup instructions
 * ════════════════════════════════════════════════════════════
 */

import { useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';

// ── OAuth Config ─────────────────────────────────────────────
// TODO §1: Add your Google Client ID to .env
// TODO §2: Add your GitHub Client ID to .env
// TODO §3: Add your LinkedIn Client ID to .env
const SOCIAL_CONFIG = {
  google: {
    clientId:    process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
    enabled:     !!(process.env.REACT_APP_GOOGLE_CLIENT_ID),
  },
  github: {
    clientId:    process.env.REACT_APP_GITHUB_CLIENT_ID || '',
    enabled:     !!(process.env.REACT_APP_GITHUB_CLIENT_ID),
    redirectUri: `${window.location.origin}/auth/callback`,
    scope:       'user:email read:user',
    authUrl:     'https://github.com/login/oauth/authorize',
  },
  linkedin: {
    clientId:    process.env.REACT_APP_LINKEDIN_CLIENT_ID || '',
    enabled:     !!(process.env.REACT_APP_LINKEDIN_CLIENT_ID),
    redirectUri: `${window.location.origin}/auth/callback`,
    scope:       'openid profile email',
    authUrl:     'https://www.linkedin.com/oauth/v2/authorization',
  },
};

export { SOCIAL_CONFIG };

// ── Build OAuth redirect URL ──────────────────────────────────
function buildOAuthUrl(provider, role) {
  const cfg = SOCIAL_CONFIG[provider];
  const state = btoa(JSON.stringify({ provider, role, ts: Date.now() }));

  // Store state + role in sessionStorage for callback page
  sessionStorage.setItem('oauth_state', state);
  sessionStorage.setItem('oauth_role',  role);

  if (provider === 'github') {
    const params = new URLSearchParams({
      client_id:    cfg.clientId,
      redirect_uri: cfg.redirectUri,
      scope:        cfg.scope,
      state,
    });
    return `${cfg.authUrl}?${params}`;
  }

  if (provider === 'linkedin') {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id:     cfg.clientId,
      redirect_uri:  cfg.redirectUri,
      scope:         cfg.scope,
      state,
    });
    return `${cfg.authUrl}?${params}`;
  }

  return null;
}

// ── Main hook ─────────────────────────────────────────────────
export function useSocialAuth() {
  const { socialLogin } = useAuth();
  const [loading, setLoading]   = useState(null); // 'google' | 'github' | 'linkedin' | null
  const [error,   setError]     = useState('');

  // ── Google ──────────────────────────────────────────────────
  const handleGoogleSuccess = useCallback(async (credentialResponse) => {
    setLoading('google');
    setError('');
    try {
      // Extract name and email from Google JWT (frontend decode, not secure for auth)
      let name = undefined;
      let email = undefined;
      try {
        const base64Url = credentialResponse.credential.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const payload = JSON.parse(jsonPayload);
        name = payload.name;
        email = payload.email;
      } catch {}
      const user = await socialLogin('google', {
        credential: credentialResponse.credential,
        name,
        email,
      });
      return user;
    } catch (err) {
      setError(err.message || 'Google sign-in failed. Please try again.');
      return null;
    } finally {
      setLoading(null);
    }
  }, [socialLogin]);

  const handleGoogleError = useCallback(() => {
    setError('Google sign-in was cancelled or failed.');
    setLoading(null);
  }, []);

  // ── GitHub ──────────────────────────────────────────────────
  const handleGitHub = useCallback((role = 'mentee') => {
    if (!SOCIAL_CONFIG.github.enabled) {
      setError('GitHub login is not configured yet. See SOCIAL_AUTH.md §2.');
      return;
    }
    setLoading('github');
    const url = buildOAuthUrl('github', role);
    window.location.href = url;
  }, []);

  // ── LinkedIn ─────────────────────────────────────────────────
  const handleLinkedIn = useCallback((role = 'mentee') => {
    if (!SOCIAL_CONFIG.linkedin.enabled) {
      setError('LinkedIn login is not configured yet. See SOCIAL_AUTH.md §3.');
      return;
    }
    setLoading('linkedin');
    const url = buildOAuthUrl('linkedin', role);
    window.location.href = url;
  }, []);

  // ── Handle callback (called from OAuthCallback page) ────────
  const handleCallback = useCallback(async (code, provider) => {
    setLoading(provider);
    setError('');
    try {
      const role = sessionStorage.getItem('oauth_role') || 'mentee';
      const user = await socialLogin(provider, { code, role });
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_role');
      return user;
    } catch (err) {
      setError(err.message || `${provider} sign-in failed.`);
      return null;
    } finally {
      setLoading(null);
    }
  }, [socialLogin]);

  return {
    handleGoogleSuccess,
    handleGoogleError,
    handleGitHub,
    handleLinkedIn,
    handleCallback,
    loading,
    error,
    clearError: () => setError(''),
    config: SOCIAL_CONFIG,
  };
}
